import {createContext, useReducer, useCallback, useMemo, useContext} from 'react';
import pl from '../translations/pl.json';
import en from '../translations/en.json';
import it from '../translations/it.json';

const translationsMap = {pl, en, it};

export const LOCALE_MAP = {
    pl: 'pl-PL',
    en: 'en-GB',
    it: 'it-IT'
};

function getInitialLanguage() {
    // URL is the source of truth: ?lang=EN or ?lang=IT
    // No ?lang param means Polish (default)
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam) {
        const normalized = langParam.toLowerCase();
        if (translationsMap[normalized]) {
            return normalized;
        }
    }
    return 'pl';
}

const initialLanguage = getInitialLanguage();

function languageReducer(state, action) {
    switch (action.type) {
        case 'setLanguage': {
            return {language: action.payload};
        }
        default:
            throw new Error(`Unknown action: ${action.type}`);
    }
}

export const LanguageContext = createContext(null);
export const LanguageDispatch = createContext(null);

export const LanguageProvider = ({children}) => {
    const [state, dispatch] = useReducer(languageReducer, {language: initialLanguage});

    const t = useCallback((key, params = {}) => {
        const keys = key.split('.');

        // Try current language
        let value = translationsMap[state.language];
        for (const k of keys) {
            value = value?.[k];
            if (value === undefined) break;
        }

        // Fallback to Polish
        if (value === undefined) {
            value = pl;
            for (const k of keys) {
                value = value?.[k];
                if (value === undefined) break;
            }
        }

        // Fallback to key itself
        if (value === undefined) return key;

        // Interpolation
        if (typeof value === 'string' && Object.keys(params).length > 0) {
            return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
                return params[paramKey] !== undefined ? params[paramKey] : match;
            });
        }

        return value;
    }, [state.language]);

    const contextValue = useMemo(() => ({
        language: state.language,
        t
    }), [state.language, t]);

    return (
        <LanguageContext.Provider value={contextValue}>
            <LanguageDispatch.Provider value={dispatch}>
                {children}
            </LanguageDispatch.Provider>
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) throw new Error('useLanguage must be used within LanguageProvider');
    return context;
};
